<template>
  <div class="thread-item" @click="navigateToDetail">
    <!-- 头部信息 -->
    <div class="thread-header">
      <Avatar
        class="avatar"
        :avatar-index="post.avatar"
        size="medium"
        :clickable="true"
        :enable-navigate="true"
        :user-id="post.authorid"
        :avatar-frame="post.avatar_frame"
      />
      <div class="user-info">
        <div class="username-wrapper">
          <span class="username">{{ post.author }}</span>
          <!-- 用户称号展示 -->
          <div
            v-if="post.user_title && post.user_title.length > 0"
            class="thread-titles"
          >
            <span
              v-for="(title, index) in post.user_title"
              :key="index"
              class="thread-title-badge"
            >
              {{ title }}
            </span>
          </div>
        </div>
        <div class="post-time-wrapper">
          <div class="post-time">{{ formattedDateline }}</div>
          <div v-if="post.is_translated === 1" class="translated-tip">
            {{ t("postDetail.translatedTo") }}
            {{ languageStore.currentLanguageName }}
          </div>
        </div>
      </div>
      <button
        class="follow-btn"
        v-if="showFollow && post.authorid !== userStore.userInfo?.uid"
        @click.stop="follow"
        :class="post.is_author_followed ? 'followed' : 'unfollowed'"
      >
        {{
          post.is_author_followed
            ? t("profile.followed")
            : "+ " + t("profile.follow")
        }}
      </button>
    </div>
    <!-- 帖子内容 -->
    <div class="thread-title" v-html="decodeHtml(post.subject)"></div>
    <!-- 帖子图片 -->
    <div
      class="thread-image-wrap"
      v-if="
        post.image_links &&
        post.image_links.length > 0 &&
        post.video_links &&
        post.video_links.length == 0
      "
    >
      <div class="image-grid">
        <template v-for="(img, index) in post.image_links.slice(0, 3)">
          <div
            v-if="
              index === 2 && post.image_links && post.image_links.length > 3
            "
            class="image-with-label"
            :key="'img-label-' + index"
          >
            <img
              class="thread-image"
              :src="img"
              :alt="'post-pic-' + (index + 1)"
              @click.stop="showImage(index)"
            />
            <div class="image-count-label">
              <img src="../../assets/image-fill.png" class="label-icon" />
              <span>{{ post.image_links.length }}</span>
            </div>
          </div>
          <img
            v-else
            :key="'img-normal-' + index"
            class="thread-image"
            :src="img"
            :alt="'post-pic-' + (index + 1)"
            @click.stop="showImage(index)"
          />
        </template>
      </div>
    </div>
    <!-- 视频预览 -->
    <div
      class="thread-video-wrap"
      v-if="post.video_links && post.video_links.length > 0"
    >
      <div class="video-preview" @click.stop="openVideoModal">
        <video
          class="video-thumbnail"
          :src="post.video_links[0]"
          preload="metadata"
          muted
          playsinline
          webkit-playsinline
          x5-playsinline
          @loadeddata="onVideoThumbnailLoaded"
          ref="thumbnailVideoRef"
        ></video>
        <div class="video-play-overlay">
          <img
            src="../../assets/video-play-2.png"
            class="play-icon"
            alt="play"
          />
        </div>
      </div>
    </div>
    <!-- 附加说明 -->
    <div
      class="thread-desc"
      v-if="post.message"
      v-html="decodeHtml(post.message)"
    ></div>
    <!-- 标签展示区域 -->
    <TagList v-if="showTag" :tags="post.tag_info || []" mode="display" />
    <!-- 互动数据 -->
    <div class="thread-actions">
      <div class="action">
        <div class="icon eye-icon"></div>
        <span>{{ post.views || 1 }}</span>
      </div>
      <div class="action">
        <div class="icon comment-icon"></div>
        <span>{{ post.replies || 0 }}</span>
      </div>
      <div class="action" @click.stop="handleLike">
        <div
          class="icon like-icon"
          :class="{ 'is-active': post.is_liked }"
        ></div>
        <span>{{ post.likes || 0 }}</span>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from "vue";
import { useImageViewerStore } from "../../stores";
import { useRouter, useRoute } from "vue-router";
import { useI18n } from "vue-i18n";
import { formatDateline, decodeHtml } from "../../utils";
import {
  useUserStore,
  useToastStore,
  useVideoStore,
  useLanguageStore,
} from "@/stores";
import { useLikeStore } from "@/stores/like";
import { useForumStore } from "@/stores/forum";
import Avatar from "./Avatar.vue";
import TagList from "./TagList.vue";
const toast = useToastStore();
const likeStore = useLikeStore();
const userStore = useUserStore();
const forumStore = useForumStore();
const videoStore = useVideoStore();
const languageStore = useLanguageStore();
const { t } = useI18n();

interface Props {
  post: any;
  showFollow?: boolean;
  showTag?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  showTag: true,
});
const route = useRoute();
const emit = defineEmits<{
  (e: "update:post", value: any): void;
}>();

// 计算格式化后的时间
const formattedDateline = computed(() => {
  return formatDateline(props.post.dateline, t);
});

// 计算头像路径

const imageViewer = useImageViewerStore();
const router = useRouter();
const showImage = (index: number) => {
  if (props.post.image_links) {
    imageViewer.show(props.post.image_links, index);
  }
};

const navigateToDetail = () => {
  // 更新浏览量
  forumStore.updateThreadViews(props.post.tid);

  router.push({
    path: `/post/${props.post.tid}`,
    query: {
      ...route.query,
    },
  });
};

const follow = async () => {
  if (!userStore.checkLogin()) {
    return;
  }
  if (props.post.is_author_followed) {
    const res = await userStore.unfollow(props.post.authorid);
    if (res?.code == 200) {
      const updatedPost = {
        ...props.post,
        is_author_followed: 0,
      };
      emit("update:post", updatedPost);
      toast.showToast(t("profile.unfollowSuccess"), "success", 1000);
    }
  } else {
    const res = await userStore.follow(props.post.authorid);
    if (res?.code == 200) {
      const updatedPost = {
        ...props.post,
        is_author_followed: 1,
      };
      emit("update:post", updatedPost);
      toast.showToast(t("profile.followSuccess"), "success", 1000);
    }
  }
};

const handleLike = async () => {
  if (!userStore.checkBind()) {
    return;
  }
  if (props.post.is_liked) {
    const res = await likeStore.cancelLike(props.post.tid, "thread");
    if (res?.code == 200) {
      const updatedPost = {
        ...props.post,
        is_liked: false,
        likes: Math.max(0, props.post.likes - 1),
      };
      emit("update:post", updatedPost);
      toast.showToast(t("profile.cancelLikeSuccess"), "success", 1000);
    }
  } else {
    const res = await likeStore.addLike(props.post.tid, "thread");
    if (res?.code == 200) {
      const updatedPost = {
        ...props.post,
        is_liked: true,
        likes: props.post.likes + 1,
      };
      emit("update:post", updatedPost);
      toast.showToast(t("profile.addLikeSuccess"), "success", 1000);
    }
  }
};

// 视频播放弹窗相关
const thumbnailVideoRef = ref<HTMLVideoElement>();

const openVideoModal = () => {
  if (props.post.video_links && props.post.video_links.length > 0) {
    videoStore.openVideoModal(props.post.video_links[0]);
  }
};

const onVideoThumbnailLoaded = () => {
  // 视频缩略图加载完成，确保显示第一帧
  if (thumbnailVideoRef.value) {
    thumbnailVideoRef.value.currentTime = 0;
  }
};

// 组件挂载后，如果是iOS设备，主动触发视频加载
onMounted(() => {
  if (props.post.video_links && props.post.video_links.length > 0) {
    // 检测是否为iOS设备
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
    if (isIOS && thumbnailVideoRef.value) {
      // iOS设备上主动加载视频
      thumbnailVideoRef.value.load();
      // 延迟设置时间，确保视频加载完成
      setTimeout(() => {
        if (thumbnailVideoRef.value) {
          thumbnailVideoRef.value.currentTime = 0;
          thumbnailVideoRef.value.pause();
        }
      }, 100);
    }
  }
});
</script>

<style lang="scss" scoped>
:deep(em) {
  color: #dd8632;
  font-style: normal;
  font-weight: 600;
  font-size: 18px;
}
.thread-item {
  display: block;
  background: #fff;
  border-radius: 15px;
  padding: 16px;
  margin: 0 auto 10px;
  max-width: 800px;
  font-size: 15px;
  color: #222;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
  transition: box-shadow 0.2s ease;
  cursor: pointer;
  &:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  }

  .thread-header {
    display: flex;
    align-items: center;
    margin-bottom: 8px;

    .avatar {
      margin-right: 10px;
    }
    .user-info {
      display: flex;
      flex-direction: column;
      .username-wrapper {
        display: flex;
        align-items: center;
        flex-wrap: wrap;
        gap: 6px;
        margin-bottom: 2px;
      }
      .username {
        font-weight: 600;
        font-size: 16px;
        margin-right: 6px;
      }
      .thread-titles {
        display: flex;
        flex-wrap: wrap;
        gap: 6px;
        align-items: center;
      }
      .thread-title-badge {
        display: inline-flex;
        align-items: center;
        padding: 4px 10px;
        font-size: 10px;
        font-weight: 500;
        color: #fff;
        background: linear-gradient(135deg, #ffb400 0%, #ff9500 100%);
        border-radius: 12px;
        box-shadow: 0 2px 6px rgba(255, 180, 0, 0.25);
        white-space: nowrap;
        line-height: 1.2;
        transition: all 0.2s ease;
        position: relative;
        overflow: hidden;
      }
      .post-time-wrapper {
        display: flex;
        align-items: center;
        gap: 8px;
        margin-top: 2px;
      }
      .post-time {
        color: #aaa;
        font-size: 10px;
      }
      .translated-tip {
        color: #999;
        font-size: 10px;
      }
    }
    .follow-btn {
      margin-left: auto;
      padding: 2px 10px;
      border: 1px solid #dd8632;
      background: transparent;
      color: #dd8632;
      border-radius: 20px;
      font-size: 12px;
      outline: none;
      transition: all 0.3s ease;
      display: flex;
      align-items: center;
      cursor: pointer;
      flex-shrink: 0;
      &.followed {
        background: #f5f5f5;
        color: #999;
        border: 1px solid #ddd;
      }

      &.unfollowed:hover {
        background: #dd8632;
        color: #fff;
      }
    }
  }
  .thread-title {
    margin-bottom: 10px;
    font-size: 16px;
    color: #222;
    font-weight: 500;
    line-height: 1.5;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .thread-image-wrap {
    width: 100%;
    display: flex;
    justify-content: flex-start;
    margin-bottom: 10px;
    .image-grid {
      display: flex;
      gap: 8px;
      width: 100%;
      .thread-image {
        width: calc((100% - 16px) / 3);
        height: 110px;
        border-radius: 8px;
        object-fit: cover;
        flex: 1;
        cursor: pointer;
      }
    }
  }
  .thread-desc {
    color: #888;
    font-size: 14px;
    margin-bottom: 10px;
    line-height: 1.5;
    display: -webkit-box;
    -webkit-line-clamp: 3;
    -webkit-box-orient: vertical;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: pre-wrap;
  }
  .tag-list-wrapper {
    margin-bottom: 10px;
  }
  .thread-video-wrap {
    position: relative;
    width: 100%;
    margin-bottom: 10px;
    border-radius: 8px;
    overflow: hidden;
    cursor: pointer;

    .video-preview {
      position: relative;
      width: 100%;
      height: 200px;
      border-radius: 8px;
      overflow: hidden;
      cursor: pointer;

      .video-thumbnail {
        width: 100%;
        height: 100%;
        object-fit: contain;
        background: #fff;
      }

      .video-fallback-image {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background-size: cover;
        background-position: center;
        background-repeat: no-repeat;
        background-color: #f5f5f5;
      }

      .video-play-overlay {
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        display: flex;
        align-items: center;
        justify-content: center;
        width: 50px;
        height: 50px;
        background: rgba(0, 0, 0, 0.6);
        border-radius: 50%;
        transition: all 0.3s ease;

        &:hover {
          background: rgba(0, 0, 0, 0.8);
          transform: translate(-50%, -50%) scale(1.1);
        }

        .play-icon {
          width: 25px;
          height: 25px;
          filter: brightness(0) invert(1);
        }
      }
    }
  }
  .thread-actions {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-top: 2px;
    .action {
      display: flex;
      align-items: center;
      color: #999;
      font-size: 13px;
      .icon {
        width: 18px;
        height: 18px;
        margin-right: 4px;
        background-size: contain;
        background-repeat: no-repeat;
        background-position: center;

        &.eye-icon {
          background-image: url("../../assets/eye.png");
        }

        &.comment-icon {
          background-image: url("../../assets/comment.png");
        }

        &.like-icon {
          background-image: url("../../assets/like.png");
          cursor: pointer;

          &.is-active {
            background-image: url("../../assets/like-active.png");
          }
        }
      }
    }
  }
}

.image-with-label {
  position: relative;
  display: flex;
  flex: 1;
  .thread-image {
    width: 100%;
    height: 110px;
    border-radius: 8px;
    object-fit: cover;
    cursor: pointer;
    display: block;
  }
  .image-count-label {
    position: absolute;
    right: 6px;
    bottom: 6px;
    background: #eee;
    color: #666;
    border-radius: 12px;
    padding: 1px 8px 1px 4px;
    display: flex;
    align-items: center;
    font-size: 12px;
    .label-icon {
      width: 16px;
      height: 16px;
      margin-right: 3px;
    }
  }
}
.image-grid {
  display: flex;
  gap: 8px;
  width: 100%;
}
</style>
